import { editUser } from '@/routes/settings/account/actions.ts'

/**
 * Single funnel for "the user's active stage is gone, drop it".
 *
 * Two callers can reach the same conclusion about the same failure in the same
 * moment: `rootLoader` (cold cache) and `useProjectStageData` — and that hook is
 * itself mounted twice on a stage route (`Root` and `ProjectsRoot`). Production
 * logs showed the resulting duplicate `PATCH /users/<id>` in the same second.
 *
 * De-duplicating HERE rather than by bookkeeping at each call site is deliberate:
 * a mount-based guard silently stops working when the router changes (it already
 * did — the settings subtree also binds a `:projectStageId` param), whereas this
 * funnel cannot be bypassed by adding another caller.
 *
 * What this guarantees is exactly one thing: concurrent callers share one PATCH.
 * It deliberately does NOT remember completed clears. A time-based cooldown was
 * tried and removed, because `getCurrStage` re-persists the stage on its own
 * success path (`getCurrStage.ts:29-32`) — so a suppressed second clear could
 * leave the stale stage persisted with nothing left to re-evaluate it. Since
 * `editUser` does not write the zustand user store, the caller-side correlation
 * check in `shouldClearActiveProjectStage` only stops repeats once `['user']` has
 * refetched; a duplicate PATCH inside that lag window is possible and is the
 * cheaper failure — the write is idempotent, whereas suppression is not.
 */
const inFlight = new Map<number, Promise<void>>()

/** Test seam: forget all de-duplication state. */
export const resetClearActiveProjectStageState = () => {
  inFlight.clear()
}

export const clearActiveProjectStageOnce = (userId: number): Promise<void> => {
  const pending = inFlight.get(userId)
  if (pending) return pending

  const promise = editUser(userId, { activeProjectStage: null })
    .then(() => undefined)
    .finally(() => {
      inFlight.delete(userId)
    })

  inFlight.set(userId, promise)
  return promise
}
